import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { AcknowledgeModal, ConfirmationModal, Modal, Text } from '@percent/lemonade'
import { useMutation } from '@percent/utility'
import type { DeleteAssignmentRuleModalProps } from './DeleteAssignmentRuleModal.types'

export function DeleteAssignmentRuleModal({
  isOpen,
  onClose,
  ruleId,
  ruleName,
  refresh
}: DeleteAssignmentRuleModalProps) {
  const { t } = useTranslation()
  const { grantProgramService } = useServices()
  const [modalState, setModalState] = useState<'success' | 'error' | null>(null)

  const [{ isPending }, { apiFunc: deleteRule }] = useMutation(
    grantProgramService.deleteAutoAssignmentRule,
    () => {
      setModalState('success')
    },
    () => {
      setModalState('error')
    }
  )

  const handleClose = () => {
    onClose()
    setModalState(null)
  }

  const handleSuccessClose = () => {
    refresh()
    handleClose()
  }

  const renderModalContent = () => {
    if (modalState === 'success') {
      return (
        <AcknowledgeModal
          result="positive"
          title={t('grants.assignmentRules.deleteModal.success.title')}
          buttonText={t('button.close')}
          handleClose={handleSuccessClose}
          viewTestId="delete-assignment-rule-success-modal"
        >
          <Text size="small">{t('grants.assignmentRules.deleteModal.success.description')}</Text>
        </AcknowledgeModal>
      )
    }

    if (modalState === 'error') {
      return (
        <AcknowledgeModal
          result="negative"
          title={t('grants.assignmentRules.deleteModal.error.title')}
          description={t('grants.assignmentRules.deleteModal.error.description')}
          buttonText={t('button.close')}
          handleClose={handleClose}
          viewTestId="delete-assignment-rule-error-modal"
        />
      )
    }

    return (
      <ConfirmationModal
        title={t('grants.assignmentRules.deleteModal.title')}
        primaryButtonText={t('button.delete')}
        secondaryButtonText={t('button.cancel')}
        loading={isPending}
        handleClose={handleClose}
        handleSubmit={() => deleteRule({ id: ruleId })}
        primaryBtnTestId="delete-assignment-rule-submit-button"
        secondaryBtnTestId="delete-assignment-rule-cancel-button"
        aria-labelledby="delete-assignment-rule-modal"
      >
        <Text>{t('grants.assignmentRules.deleteModal.description', { ruleName })}</Text>
      </ConfirmationModal>
    )
  }

  return (
    <Modal open={isOpen} onClose={handleClose} aria-labelledby="delete-assignment-rule-modal">
      {renderModalContent()}
    </Modal>
  )
}
